const STORAGE_KEY = 'appBuildCommit';

export const clearAllCaches = async (): Promise<void> => {
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  }
};

const checkVersionAndClearCache = async (): Promise<boolean> => {
  try {
    const response = await fetch(`/build-meta.json?t=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) return false;

    const meta = await response.json();
    const serverCommit = meta.commit;

    if (!serverCommit || serverCommit === 'unknown') return false;

    const savedCommit = localStorage.getItem(STORAGE_KEY);

    if (savedCommit && savedCommit !== serverCommit) {
      localStorage.setItem(STORAGE_KEY, serverCommit);
      await clearAllCaches();
      return true;
    }

    localStorage.setItem(STORAGE_KEY, serverCommit);
    return false;
  } catch {
    return false;
  }
};

export { checkVersionAndClearCache };

/** Reload an outdated bundle once when its lazy chunk cannot be loaded.
 * Same-build/network failures stay on the recoverable error screen. */
export const recoverChunkLoad = async (
  error: Error,
  clientCommit = process.env.REACT_APP_BUILD_SHA,
): Promise<boolean> => {
  if (
    typeof window === 'undefined' ||
    !/^[a-f0-9]{40}$/i.test(clientCommit || '') ||
    !(
      error?.name === 'ChunkLoadError' ||
      /Loading (?:CSS )?chunk .+ failed/i.test(error?.message || '')
    )
  )
    return false;
  const key = `chunkRecovery:${clientCommit}`;
  try {
    if (sessionStorage.getItem(key)) return false;
    const response = await fetch('/build-meta.json', { cache: 'no-store' });
    if (!response.ok) return false;
    const { commit } = await response.json();
    if (
      !/^[a-f0-9]{40}$/i.test(commit || '') ||
      commit === clientCommit ||
      sessionStorage.getItem(key)
    )
      return false;
    // Mark before clearing caches; nested boundaries cannot start a reload loop.
    sessionStorage.setItem(key, commit);
    try {
      localStorage.setItem(STORAGE_KEY, commit);
    } catch {
      /* Optional. */
    }
    await clearAllCaches().catch(() => undefined);
    return true;
  } catch {
    // If storage or the version check is unavailable, leave manual recovery.
    return false;
  }
};
